import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const AnnualNumbers = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const donationBreakdown = [
    {
      category: "Rural Women Empowerment Program",
      percentage: 53,
      description: "Empowering rural women through education, skills training, and economic opportunities. Supporting women-led initiatives and promoting gender equality in remote villages.",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
        </svg>
      ),
      color: "from-pink-500 to-rose-500"
    },
    {
      category: "Mental Health and Awareness Program",
      percentage: 32,
      description: "Providing mental health support and awareness programs to rural communities, breaking stigma and offering counseling services to those in need.",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"></path>
        </svg>
      ),
      color: "from-purple-500 to-indigo-500"
    },
    {
      category: "Emergency Relief",
      percentage: 15,
      description: "Rapid response and support during natural disasters and emergencies, including water filter distribution. Providing immediate aid, shelter, food, clean water, and medical assistance to affected communities.",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
        </svg>
      ),
      color: "from-red-500 to-orange-500"
    }
  ];

  return (
    <section className="relative py-24 bg-gradient-to-br from-neutral-900 via-black to-neutral-900 overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 bg-dot-black opacity-30"></div>
      <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-gradient-to-r from-amber-500/10 to-yellow-500/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-1/4 left-1/4 w-80 h-80 bg-gradient-to-r from-yellow-500/10 to-amber-500/10 rounded-full blur-3xl"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-6xl font-bold mb-6">
            <span className="text-gradient">
              How We Use Your Donations
            </span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-amber-400 to-yellow-600 mx-auto rounded-full mb-8"></div>
          <p className="text-xl text-neutral-300 max-w-3xl mx-auto">
            Complete transparency in how your contributions create lasting impact across our programs in Nepal.
          </p>
        </motion.div>

        {/* Donation Breakdown Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {donationBreakdown.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="relative group"
            >
              <div className="glass rounded-2xl p-8 h-full group-hover:scale-105 transition-all duration-500">
                {/* Icon and Percentage */}
                <div className="flex items-center justify-between mb-6">
                  <div className="text-amber-400">
                    {item.icon}
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-white">{item.percentage}%</div>
                    <div className="text-amber-400 text-sm">of donations</div>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mb-6">
                  <div className="w-full bg-neutral-700 rounded-full h-3">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: `${item.percentage}%` }}
                      transition={{ duration: 1.5, delay: index * 0.2 }}
                      viewport={{ once: true }}
                      className={`h-3 rounded-full bg-gradient-to-r ${item.color}`}
                    ></motion.div>
                  </div>
                </div>

                {/* Content */}
                <h3 className="text-xl font-bold text-white mb-4">{item.category}</h3>
                <p className="text-neutral-300 text-sm leading-relaxed mb-4">{item.description}</p>
                {item.category === "Rural Women Empowerment Program" && (
                  <Link
                    to="/rural-women-empowerment"
                    className="inline-flex items-center space-x-2 text-amber-400 hover:text-amber-300 text-sm font-semibold transition-colors"
                  >
                    <span>View Success Story</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                    </svg>
                  </Link>
                )}
                {item.category === "Mental Health and Awareness Program" && (
                  <Link
                    to="/mental-health-awareness"
                    className="inline-flex items-center space-x-2 text-purple-400 hover:text-purple-300 text-sm font-semibold transition-colors"
                  >
                    <span>View Annual Report</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                    </svg>
                  </Link>
                )}
              </div>

              {/* Glow effect */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-amber-600/20 to-yellow-600/20 blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10"></div>
            </motion.div>
          ))}
        </div>

        {/* Summary Statistics */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          viewport={{ once: true }}
          className="glass rounded-3xl p-8 md:p-12"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-4xl md:text-5xl font-bold text-gradient mb-2">100%</div>
              <div className="text-neutral-300">Transparency</div>
              <div className="text-neutral-400 text-sm mt-2">Every dollar accounted for</div>
            </div>
            <div>
                          <div className="text-4xl md:text-5xl font-bold text-gradient mb-2">5%</div>
            <div className="text-neutral-300">Administrative Overhead</div>
                              <div className="text-neutral-400 text-sm mt-2">Low overhead, maximum impact</div>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-bold text-gradient mb-2">42</div>
              <div className="text-neutral-300">Villages Served</div>
              <div className="text-neutral-400 text-sm mt-2">Across remote Nepal</div>
            </div>
          </div>
        </motion.div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <h3 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Make an Impact?
          </h3>
          <p className="text-xl text-neutral-300 mb-8 max-w-2xl mx-auto">
            Your donation will be allocated transparently across these vital programs to create maximum impact.
          </p>
          <button onClick={() => scrollToSection('donate')} className="relative px-8 py-4 bg-gradient-to-r from-amber-600 to-yellow-600 text-white font-semibold rounded-lg btn-shine transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-amber-500/25">
            <span className="relative z-10">Donate Now</span>
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default AnnualNumbers; 